import type { WorkProfileStats } from './types';

/**
 * Work Mode — gamification math.
 *
 * Everything here is derived client-side from `work_profile_stats`, so the
 * rules can be tuned without a migration. The weighting is deliberate:
 * output earns points, but QUALITY and SAFETY earn far more per action. A
 * worker who reports a defect, flags a hazard or gets an instruction fixed
 * should always come out ahead of one who quietly rushes units.
 */

export const POINTS = {
  unit: 10,
  defectReported: 40,
  safetyNote: 60,
  suggestion: 30,
  suggestionAdopted: 100,
  kudos: 15,
  checkin: 5,
  streakMilestone: 20, // every 5 consecutive check-ins
} as const;

export type AchievementTier = 'bronze' | 'silver' | 'gold';
export type AchievementCategory = 'quality' | 'safety' | 'voice' | 'output' | 'wellbeing';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  category: AchievementCategory;
  tier: AchievementTier;
  earned: boolean;
  /** 0..1 toward earning it (1 when earned). */
  progress: number;
  /** Short "3 / 10" style caption. */
  caption: string;
}

export interface LevelInfo {
  level: number;
  title: string;
  points: number;
  /** Points at the start of this level. */
  floor: number;
  /** Points needed to reach the next level, or null at the cap. */
  next: number | null;
  /** 0..1 progress inside the current level. */
  progress: number;
}

const LEVELS: Array<{ at: number; title: string }> = [
  { at: 0, title: 'Apprentice' },
  { at: 100, title: 'Builder' },
  { at: 300, title: 'Craftsperson' },
  { at: 700, title: 'Specialist' },
  { at: 1500, title: 'Expert' },
  { at: 3000, title: 'Master builder' },
  { at: 6000, title: 'Line lead' },
  { at: 10000, title: 'Quality guardian' },
  { at: 16000, title: 'Sensei' },
  { at: 25000, title: 'Legend of the floor' },
];

/** Scrap rate as a fraction of everything that left the bench. */
export function scrapRate(good: number, scrap: number): number | null {
  const total = good + scrap;
  if (total === 0) return null;
  return scrap / total;
}

/** Multiplier applied to unit points: clean work is worth more. */
export function qualityMultiplier(stats: Pick<WorkProfileStats, 'units_total' | 'scrap_total'>): number {
  const rate = scrapRate(stats.units_total, stats.scrap_total);
  if (rate === null || stats.units_total < 10) return 1;
  if (rate <= 0.02) return 1.25;
  if (rate <= 0.05) return 1.1;
  if (rate > 0.1) return 0.85;
  return 1;
}

export function computePoints(stats: WorkProfileStats): number {
  const unitPoints = stats.units_total * POINTS.unit * qualityMultiplier(stats);
  const total =
    unitPoints +
    stats.defects_reported * POINTS.defectReported +
    stats.safety_total * POINTS.safetyNote +
    stats.suggestions_total * POINTS.suggestion +
    stats.suggestions_adopted * POINTS.suggestionAdopted +
    stats.kudos_total * POINTS.kudos +
    stats.checkins_total * POINTS.checkin +
    Math.floor(stats.checkin_streak / 5) * POINTS.streakMilestone;
  return Math.round(total);
}

export function levelFor(points: number): LevelInfo {
  let idx = 0;
  for (let i = 0; i < LEVELS.length; i++) {
    if (points >= LEVELS[i].at) idx = i;
  }
  const cur = LEVELS[idx];
  const nxt = LEVELS[idx + 1] ?? null;
  const span = nxt ? nxt.at - cur.at : 1;
  return {
    level: idx + 1,
    title: cur.title,
    points,
    floor: cur.at,
    next: nxt ? nxt.at : null,
    progress: nxt ? Math.min(1, (points - cur.at) / span) : 1,
  };
}

interface Rule {
  id: string;
  title: string;
  description: string;
  category: AchievementCategory;
  tier: AchievementTier;
  value: (s: WorkProfileStats) => number;
  target: number;
  /** Extra gate (e.g. "and zero scrap"). */
  gate?: (s: WorkProfileStats) => boolean;
  captionUnit?: string;
}

const RULES: Rule[] = [
  // Quality — the headline act.
  { id: 'eagle_eye', title: 'Eagle eye', description: 'Reported your first defect instead of letting it slide.', category: 'quality', tier: 'bronze', value: (s) => s.defects_reported, target: 1 },
  { id: 'inspector', title: 'Inspector', description: 'Ten defects caught at the bench.', category: 'quality', tier: 'silver', value: (s) => s.defects_reported, target: 10 },
  { id: 'clean_sheet', title: 'Clean sheet', description: '20+ units in the last 30 days with zero scrap.', category: 'quality', tier: 'gold', value: (s) => s.units_30d, target: 20, gate: (s) => s.scrap_30d === 0 },
  // Safety — worth the most.
  { id: 'safety_first', title: 'Safety first', description: 'Flagged a hazard before it hurt anyone.', category: 'safety', tier: 'silver', value: (s) => s.safety_total, target: 1 },
  { id: 'guardian', title: 'Floor guardian', description: 'Five safety concerns raised.', category: 'safety', tier: 'gold', value: (s) => s.safety_total, target: 5 },
  // Voice — telling management what to change.
  { id: 'idea_spark', title: 'Idea spark', description: 'Suggested an improvement, a tool, or an instruction fix.', category: 'voice', tier: 'bronze', value: (s) => s.suggestions_total, target: 1 },
  { id: 'changemaker', title: 'Changemaker', description: 'One of your suggestions was adopted.', category: 'voice', tier: 'gold', value: (s) => s.suggestions_adopted, target: 1 },
  { id: 'kind_heart', title: 'Team spirit', description: 'Three shout-outs for your crew.', category: 'voice', tier: 'bronze', value: (s) => s.kudos_total, target: 3 },
  // Output.
  { id: 'first_build', title: 'First build', description: 'Your first finished unit.', category: 'output', tier: 'bronze', value: (s) => s.units_total, target: 1 },
  { id: 'hot_hands', title: 'Hot hands', description: 'Ten units in a single day.', category: 'output', tier: 'bronze', value: (s) => Math.max(s.units_today, s.best_day_units), target: 10 },
  { id: 'century', title: 'Century', description: 'One hundred units, lifetime.', category: 'output', tier: 'silver', value: (s) => s.units_total, target: 100 },
  { id: 'thousand', title: 'Thousand club', description: 'A thousand units, lifetime.', category: 'output', tier: 'gold', value: (s) => s.units_total, target: 1000 },
  { id: 'cross_trained', title: 'Cross-trained', description: 'Worked at three different stations.', category: 'output', tier: 'silver', value: (s) => s.stations_worked, target: 3 },
  // Wellbeing.
  { id: 'regular', title: 'Regular', description: 'Checked in five days in a row.', category: 'wellbeing', tier: 'bronze', value: (s) => s.checkin_streak, target: 5 },
  { id: 'rock_steady', title: 'Rock steady', description: 'Checked in twenty days in a row.', category: 'wellbeing', tier: 'silver', value: (s) => s.checkin_streak, target: 20 },
  { id: 'veteran', title: 'Veteran', description: 'Fifty days on the floor.', category: 'wellbeing', tier: 'gold', value: (s) => s.days_active, target: 50 },
];

export function computeAchievements(stats: WorkProfileStats): Achievement[] {
  return RULES.map((r) => {
    const v = r.value(stats);
    const gateOk = r.gate ? r.gate(stats) : true;
    const earned = v >= r.target && gateOk;
    const progress = earned ? 1 : Math.max(0, Math.min(0.99, v / r.target));
    return {
      id: r.id,
      title: r.title,
      description: r.description,
      category: r.category,
      tier: r.tier,
      earned,
      progress,
      caption: `${Math.min(v, r.target)} / ${r.target}`,
    };
  });
}

export const CATEGORY_LABEL: Record<AchievementCategory, string> = {
  quality: 'Quality',
  safety: 'Safety',
  voice: 'Your voice',
  output: 'Output',
  wellbeing: 'Wellbeing',
};

/**
 * Efficiency against a target cycle time: 1.0 = exactly on target, >1 is
 * faster. Null when there's nothing to compare.
 */
export function efficiency(avgSeconds: number | null | undefined, targetSeconds: number | null | undefined): number | null {
  if (!avgSeconds || !targetSeconds) return null;
  return targetSeconds / avgSeconds;
}

export type PaceTone = 'ahead' | 'on' | 'behind' | 'none';

export function paceTone(eff: number | null): PaceTone {
  if (eff === null) return 'none';
  if (eff >= 1.05) return 'ahead';
  if (eff >= 0.85) return 'on';
  return 'behind';
}
